<template>
  <div id="bio-project-materials" class="flex-column">
    <div class="bio-project-selector">
      <VHeader>
        <template #project>
          {{ projectTitle }}
        </template>
      </VHeader>
      <multi-select-filter
        v-if="!disableProjectSelector"
        :get-value="groupProjects"
        id="project-sel"
        display-value="title"
        :disable="disableProjectSelector"
        :current-set-value="selectedProject"
        :multiple="false"
        :update-method="updateProject"
        filter-class="bio-project-selector-filter"
        label-class="bio-project-selector-label"
        track-by="id"
      />
      <VHeader
        class="bio-project-selector-project-number"
        v-if="!disableProjectSelector && projectLength"
      >
        <template #lower-subtitle>
          {{ projectSubtitle }}
        </template>
      </VHeader>
    </div>

    <div id="bio-project-materials-filter-wrapper">
      <b-form>
        <fieldset class="bio-project-materials-filters">
          <b-container fluid>
            <b-row :style="{ height: rowHeight + 'em' }">
              <b-col :class="isInBioTrackingStyle">
                <short-text-filter
                  id="flt-inp"
                  label="Material Name"
                  :get-value="nameFilter"
                  :update-method="updateMaterialFilters"
                  path="material-name"
                  placeholder="Material Name"
                />
              </b-col>
              <b-col :class="isInBioTrackingStyle">
                <multi-select-filter
                  :get-value="bioMaterialBundles"
                  id="material-type-sel"
                  label="Material Type"
                  path="material-type"
                  :current-set-value="selectedMatType"
                  :multiple="true"
                  track-by="id"
                  display-value="label"
                  :update-method="updateMaterialFilters"
                />
              </b-col>
              <b-col :class="isInBioTrackingStyle">
                <multi-select-filter
                  :get-value="gmoOptions"
                  id="material-type-sel"
                  label="GMO?"
                  path="gmo-sel"
                  :current-set-value="selectedGMOOption"
                  :multiple="true"
                  track-by="id"
                  display-value="label"
                  :update-method="updateMaterialFilters"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col :class="isInBioTrackingStyle">
                <multi-select-filter
                  :get-value="bioContainmentLevels"
                  id="material-type-sel"
                  label="Risk/Containment Level"
                  display-value="label"
                  path="containment-level"
                  :current-set-value="selectedContainment"
                  :update-method="updateMaterialFilters"
                  track-by="id"
                />
              </b-col>
            </b-row>
          </b-container>
        </fieldset>
      </b-form>
    </div>
    <BaseTable
      :items="filteredBioMaterials"
      :fields="fields"
      :total-items="filteredBioMaterials.length"
      :table-page-at="resultsPerPage"
      :items-per-page="resultsPerPage"
      :current-page="currentPage"
      :no-provider-paging="true"
      :custom-headers="['select_all']"
      :no-local-sorting="false"
      :is-busy="isBusy"
      @sort-changed="sortingChangedEvent"
      class-prefix="bio-pro-mat"
      @update-current-page="updateCurrentPage"
      ref="table"
      default-sort="type_human_name"
    >
      <template #select-all="row">
        <b-form-checkbox
          name="checkbox-1"
          v-model="row.data.item.selected"
          :id="checkboxId(row)"
          @change="unsavedChanges = true"
        />
      </template>
      <template #name_view="row">
        {{ row.data.item.name }}
        <BioMaterialDataWindow
          :material-type="row.data.item.type"
          :material-type-label="row.data.item.type_human_name"
          :bio-uuid="row.data.item.material_uuid"
          :additional-id="row.data.item.material_id"
          :group-nid="groupNid"
        />
      </template>
      <template #select_all-header="data">
        <b-form-checkbox
          name="select-all-bio-materials"
          v-model="selectAll"
          @change="selectedAllIgnoredToggle()"
        >
          Select All
        </b-form-checkbox>
      </template>
      <template #empty>
        <div class="biologicals-empty-text">
          There are currently no biological materials to list.
        </div>
      </template>
    </BaseTable>
    <!-- If OUTSIDE of BIOWIZARD -->
    <a
      v-if="!isInBioWizard"
      @click.prevent="displaySearchAddModal"
      href="#"
      id="add-bio-materials-trigger"
      >Add biological material to {{ groupTitle }}</a
    >
    <!-- If INSIDE of BIOWIZARD -->
    <SearchAddFromLibraryModal
      v-if="isInBioWizard && bioMaterialBundles.length > 0"
      :bio-material-bundles="bioMaterialBundles"
      :group-title="groupTitle"
      :group-nid="groupNid"
      :enable-bio-material-type-select="true"
    />
    <div class="bio-project-selector-actions-wrapper floating-save-bar">
      <div class="bio-project-selector-action-buttons">
        <div class="first-set-action">
          <div class="first-set-action-item">
            <div v-if="!unsavedChanges">
              Please press 'Save Changes' before navigating away from this page,
              or selecting another Research Project to avoid losing unsaved
              changes.
            </div>
            <VBanner
              color="warning"
              alt="Unsaved changes detected"
              id="banner"
              v-if="unsavedChanges"
              >Please press 'Save Changes' before navigating away from this
              page, or selecting another Research Project to avoid losing
              unsaved changes.</VBanner
            >
          </div>
          <div class="first-set-action-item">
            <VButton
              class="primary"
              @click="saveChanges"
              :enable-prevent="true"
            >
              Save Changes
            </VButton>
          </div>
        </div>
        <div class="second-set-actions" v-if="displaySecondSet">
          <VButton id="done" class="clear" @click="emitCloseModal">
            Done
          </VButton>
          <VButton
            v-if="specifyLink"
            id="specify-usage"
            class="clear"
            @click="navigateToSpecifyUsage"
          >
            Specify Usage
          </VButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import { useQueryString } from "@/composables/useQueryString";
import BaseTable from "../raft/BaseTable.vue";
import BiologicalsListingStore from "../../store/modules/biologicals_listing";
import BioMaterialDataWindow from "./BioMaterialDataWindow.vue";
import MultiSelectFilter from "../filters/MultiSelectFilter.vue";
import { restClient, jsonClient } from "../../lib/raftApi";
import ShortTextFilter from "../filters/ShortTextFilter.vue";
import VButton from "../raft/VButton.vue";
import VHeader from "../raft/VHeader.vue";
import VBanner from "../raft/VBanner.vue";

const { config } = useDrupalSettings();

export default {
  name: "BioProjectMaterials",
  components: {
    BaseTable,
    MultiSelectFilter,
    ShortTextFilter,
    BioMaterialDataWindow,
    VButton,
    VHeader,
    VBanner,
    SearchAddFromLibraryModal: () => import("./SearchAddFromLibraryModal.vue"),
  },
  props: {
    groupNid: { type: String, required: true },
    disableProjectSelector: { type: Boolean, default: false },
    defaultProjectNid: { type: String, default: "" },
    displaySearchAdd: { type: Boolean, default: false },
  },
  data() {
    return {
      bioMaterialBundles: [],
      resultsPerPage: 25,
      isBusy: false,
      unsavedChanges: false,
      bioMaterials: [],
      filteredBioMaterials: [],
      response: null,
      fieldData: null,
      currentPage: 1,
      groupProjects: [],
      selectedProject: null,
      selectedGMOOption: null,
      selectAll: null,
      selectedContainment: null,
      selectedRiskLevel: null,
      selectedMatType: [],
      nameFilter: "",
      gmoOptions: [
        { id: "Yes - Generated", label: "Yes - Generated" },
        { id: "Yes - Acquired", label: "Yes - Acquired" },
        { id: "No", label: "No" },
      ],
      bioContainmentLevels: [
        { id: "1", label: "1" },
        { id: "2", label: "2" },
        { id: "2+", label: "2+" },
        { id: "3", label: "3" },
        { id: "4", label: "4" },
      ],
      bioRiskLevels: [
        { id: "1", label: "1" },
        { id: "2", label: "2" },
        { id: "3", label: "3" },
        { id: "4", label: "4" },
      ],
      displayAfterSavedChanges: false,
    };
  },
  methods: {
    updateCurrentPage(newPage) {
      this.currentPage = newPage;
    },
    errorEmit(message, data) {
      this.$emit("form-error-message", { message: message, data: data });
    },
    async updateProject(newProject) {
      this.selectedProject = newProject;
      await this.getBioMaterials();
      this.filterBioMaterials();
    },
    updateMaterialFilters(newFilterItem, path) {
      if (path === "material-type") {
        this.selectedMatType = newFilterItem;
      }
      if (path === "gmo-sel") {
        this.selectedGMOOption = newFilterItem;
      }
      if (path === "containment-level") {
        this.selectedContainment = newFilterItem;
      }
      if (path === "risk-level") {
        this.selectedRiskLevel = newFilterItem;
      }
      if (path === "material-name") {
        this.nameFilter = newFilterItem;
      }
      this.filterBioMaterials();
      this.currentPage = 1;
    },
    filterBioMaterials() {
      this.selectAll = false;
      this.filteredBioMaterials = [];
      this.bioMaterials.forEach((material) => {
        if (
          this.materialMatchesNameFilter(material) &&
          this.materialGMOFilter(material) &&
          this.materialTypeFilter(material) &&
          this.materialContainmentFilter(material) &&
          this.materialGMORiskFilter(material)
        ) {
          this.filteredBioMaterials.push(material);
        }
      });
    },
    async saveChanges() {
      const patchData = {};
      this.filteredBioMaterials.forEach((material) => {
        patchData[material.material_id.toString()] = +material.selected;
      });
      const patchPath = `/${this.groupNid}/relate-project-bio-materials/${this.selectedProject?.drupal_internal__nid}`;
      // This should get automatic group context.
      await restClient
        .patch({ path: patchPath, queries: {}, data: patchData })
        .catch((e) => this.handleSaveChangeError(e));
      this.unsavedChanges = false;
      this.displayAfterSavedChanges = true;
      this.$showNotification(
        "success",
        "Biological Material selections have been saved.",
      );
    },
    handleSaveChangeError(message, data) {
      this.errorEmit(message, data);
    },
    materialMatchesNameFilter(material) {
      if (!this.nameFilter || this.nameFilter.length === 0) {
        return true;
      }
      return material.name
        .toLowerCase()
        .includes(this.nameFilter.toLowerCase());
    },
    materialGMOFilter(material) {
      if (!this.selectedGMOOption || this.selectedGMOOption.length < 1) {
        return true;
      }
      const selectedOptions = this.selectedGMOOption.map((el) => el.id);
      return selectedOptions.includes(material.gmo);
    },
    materialTypeFilter(material) {
      if (!this.selectedMatType || this.selectedMatType.length < 1) {
        return true;
      }
      const selectedOptions = this.selectedMatType.map((el) => el.id);
      return selectedOptions.includes(material.type);
    },
    materialContainmentFilter(material) {
      if (!this.selectedContainment || this.selectedContainment.length < 1) {
        return true;
      }
      const selectedOptions = this.selectedContainment.map((el) => el.id);
      return selectedOptions.includes(material.research_bsl);
    },
    materialGMORiskFilter(material) {
      if (!this.selectedRiskLevel || this.selectedRiskLevel.length < 1) {
        return true;
      }
      const selectedOptions = this.selectedRiskLevel.map((el) => el.id);
      return selectedOptions.includes(material.gmo_risk);
    },
    projectFilter(material) {
      return (
        material.project_nid === this.selectedProject?.drupal_internal__nid ||
        !material.project_nid
      );
    },
    async getResearchProjects() {
      const options = {
        path: "/node/bio_research_project",
        queries: this.bioProjectQueries,
      };
      this.groupProjects = await jsonClient
        .getAllResults(options)
        .catch((e) => {
          this.$showNotification("error", e.message);
          this.errorEmit(e.message, e.data);
        });

      if (this.disableProjectSelector) {
        // In Bio Wizard, set only project as default
        if (this.groupProjects?.length === 1) {
          this.updateProject(this.groupProjects?.[0]);
          return;
        }
      } else if (this.groupProjects?.length >= 1) {
        // If there is more than one project, set first as default.
        this.updateProject(this.groupProjects?.[0]);
        return;
      }

      // Query param to set default project.
      const defaultProjectNid = this.defaultProjectNid
        ? parseInt(this.defaultProjectNid, 10)
        : 0;
      if (defaultProjectNid) {
        this.groupProjects?.forEach((project) => {
          if (project.drupal_internal__nid === defaultProjectNid) {
            this.updateProject(project);
          }
        });
      }
    },
    async getBioMaterials() {
      this.bioMaterials = [];
      this.filteredBioMaterials = [];
      const uniquedata = {};
      const queries = {};
      queries["rog-id"] = this.groupNid;
      queries["rog-type"] = "node";
      const path = `/${this.groupNid}/bio-material-project-associations`;
      // @todo RAFT-5869 we need to fix the back-end data source. The View
      // is sending all the bio_project_material type data for all projects for
      // the group plus data for any bio materials that are not used by any
      // project. This makes us need the de-duplication code below so we get
      // the same, unique list of bio materials each time a project is selected
      // while correctly including any existing bio_project_material data for
      // that project.
      const associationsResponseData = await restClient
        .get({ path, queries })
        .catch(({ message, data }) => this.errorEmit(message, data));
      associationsResponseData.forEach((nodeData) => {
        const materialId = nodeData.material_id;
        if (!uniquedata[materialId] || !uniquedata[materialId].metadata_id) {
          uniquedata[materialId] = nodeData;
        }
        if (
          uniquedata[materialId].project_nid.toString() !==
          this.selectedProject?.drupal_internal__nid?.toString()
        ) {
          uniquedata[materialId].project_nid = "";
          uniquedata[materialId].project_title = "";
          uniquedata[materialId].metadata_id = "";
          uniquedata[materialId].material_operative_status = "";
          uniquedata[materialId].material_published_status = "";
        }
      });
      Object.values(uniquedata).forEach((nodeData, index) => {
        nodeData.bio_mat_index = index;
        // Handle setting initial check box selection.
        if (
          nodeData.project_nid.toString() ===
            this.selectedProject?.drupal_internal__nid?.toString() &&
          nodeData.material_operative_status === "1"
        ) {
          nodeData.selected = true;
        }
        // Allow for custom table control to insert checkbox.
        nodeData.select_all = {
          id: nodeData.material_uuid,
          custom_slot: "select-all",
        };
        // Allow for custom table control to insert node previews.
        nodeData.name_view = {
          text: nodeData.name,
          custom_slot: "name_view",
        };
        this.bioMaterials.push(nodeData);
      });
      this.filteredBioMaterials = this.bioMaterials;
    },
    checkboxId(row) {
      return "checkbox-" + row.data.item.material_id;
    },
    selectedAllIgnoredToggle() {
      let updated = false;
      this.filteredBioMaterials.forEach((mat) => {
        if (this.selectAll !== null && mat.selected !== this.selectAll) {
          mat.selected = this.selectAll;
          updated = true;
        }
      });
      if (updated) {
        this.unsavedChanges = true;
      }
    },
    sortingChangedEvent() {
      // No custom sorting, currently handled by b-table.
    },
    emitCloseModal() {
      this.$emit("close-modal");
    },
    navigateToSpecifyUsage() {
      window.location.href = this.specifyLink;
    },
    displaySearchAddModal() {
      this.updateQueryString("modal", "SearchAddFromLibraryModal");
      this.updateQueryString("bundles", this.bioMaterialBundles);
    },
  },
  computed: {
    hasProjects() {
      return this.groupProjects && this.projectLength > 0;
    },
    groupTitle() {
      if (this.hasProjects) {
        return this.groupProjects[0].og_audience[0]?.title;
      }
      return "group";
    },
    selectedTitle() {
      return this.selectedProject?.title || "";
    },
    fields: function () {
      return [
        {
          key: "select_all",
          label: "Select All",
          sortable: false,
        },
        {
          key: "type_human_name",
          label: "Type",
          sortable: true,
        },
        {
          key: "name_view",
          label: "Name",
          sortable: true,
        },
        {
          key: "research_bsl",
          label: "Containment Level",
          sortable: true,
        },
        {
          key: "gmo",
          label: "GMO",
          sortable: true,
        },
      ];
    },
    rowHeight() {
      let multiplier = 2.5;
      if (this.selectedMatType.length === 0) {
        return 0;
      }
      if (this.selectedMatType.length > 5) {
        multiplier = 2.2;
      }
      if (this.selectedMatType.length === 1) {
        multiplier = 3;
      }
      return this.selectedMatType.length * multiplier + 2;
    },
    alertVersion() {
      if (this.unsavedChanges) {
        return "danger";
      }
      return "default";
    },
    namespace() {
      return "BioProjectMaterials" + this.groupNid;
    },
    projectSelectLabel() {
      return `Project: ${this.selectedTitle}`;
    },
    projectLength() {
      return this.groupProjects?.length;
    },
    projectSubtitle() {
      if (this.projectNumber === 0) {
        return "Please select a project.";
      }
      return `Project ${this.projectNumber} of ${this.projectLength}`;
    },
    projectNumber() {
      for (let i = 0; i < this.groupProjects.length; i++) {
        if (this.groupProjects[i].title === this.selectedTitle) {
          return i + 1;
        }
      }
      return 0;
    },
    specifyLink() {
      if (this.displayAfterSavedChanges && config.bioTrackingSpecifyUsage) {
        return `/node/${this.groupNid}/biologicals/projects/specify-usage?gids[]=${this.groupNid}&destination=${this.destinationQuery}`;
      }
      return "";
    },
    displaySecondSet() {
      return this.displayAfterSavedChanges && !this.isInBioWizard;
    },
    projectTitle() {
      if (this.defaultProjectNid) {
        return this.projectSelectLabel;
      }
      return "Project Title";
    },
    isInBioWizard() {
      return this.destinationQuery.includes("/setup/step");
    },
    isInBioTrackingStyle() {
      return this.destinationQuery.includes("/biologicals/bio-tracking")
        ? "raft-js-filter-cols-fix"
        : "raft-js-filter-cols";
    },
    destinationQuery() {
      return window.location.pathname || "";
    },
    bioProjectQueries() {
      return {
        "filter[status][condition][path]": "status",
        "filter[status][condition][value]": 1,
        "filter[active][condition][path]": "operative_status",
        "filter[active][condition][value]": 1,
        "filter[group][condition][path]": "og_audience.drupal_internal__nid",
        "filter[group][condition][value]": this.groupNid,
        include: "og_audience",
        "fields[node--laboratory]": "title,drupal_internal__nid",
        "rog-id": this.groupNid,
        "rog-type": "node",
        sort: "title,-created",
      };
    },
  },
  async mounted() {
    this.getResearchProjects();
    await this.$store.dispatch(this.namespace + "/fetchBioMaterialBundles");
    this.bioMaterialBundles = this.$get("getBioMaterialBundles");
  },
  created() {
    const store = this.$store;
    if (!this.$store.hasModule(this.namespace)) {
      this.$store.registerModule(this.namespace, BiologicalsListingStore());
    }
    this.$get = (prop) => store.getters[this.namespace + "/" + prop];
  },
  setup() {
    const { updateQueryString } = useQueryString();
    return {
      updateQueryString,
    };
  },
};
</script>

<style lang="css" scoped src="../../css/B3BaseTable.css"></style>
<style lang="css" scoped src="../../css/B3Pagination.css"></style>

<style>
div.modal-content {
  padding: 0.5rem;
}

#bio-research-project-material-association-setup-page > div > div.form-item {
  margin-top: -1.5rem;
}

.setup_system {
  margin-top: 0;
}

#banner > .banner-top > .banner-view > .banner-slot {
  font-size: var(--font-size-50);
}
</style>

<style scoped>
.bio-project-selector-project-number {
  padding: 0.25rem;
  line-height: normal;
}
.flex-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.bio-project-selector-actions-wrapper {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
}
.bio-project-selector-action-buttons {
  margin: 1rem 0;
  display: flex;
  flex-direction: column;
  width: 100%;
}
.first-set-action {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.first-set-action-item {
  width: 70%;
}
.first-set-action-item:nth-child(2) {
  max-width: fit-content;
}
.second-set-actions > #done,
.second-set-actions > #specify-usage {
  color: var(--blue-900);
  text-decoration: underline;
}
.second-set-actions {
  padding-top: 1rem;
  display: flex;
  flex-direction: row;
  justify-content: center;
}
.alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}
.alert {
  position: relative;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
}
.alert-danger {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
}

#raft-js .form-group label,
label#project-sel-label__BV_label_ {
  font-size: var(--font-size-100, 1rem);
}

#bio-research-project-material-association-setup-page
  .row:has(.raft-js-filter-cols) {
  display: flex;
}

.row:has(.raft-js-filter-cols-fix) {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr;
  gap: 0.5rem;
}

.container-fluid:has(.row) {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(auto-fill, 54px);
}

:is(#raft-js, #raft-js-app) #bio-project-materials .raft-js-filter-cols {
  width: 100%;
  float: none;
  margin: 0 0;
}
</style>
